// Eskom PARCC — RTM persistence adapter (Phase 2 in-app lane).
//
// Bridges the pure auto-draft `RtmDocModel` (from buildRtmDocModel) and the
// persisted `RtmReport` + `RtmFinding` rows the composer edits. Three pure steps:
//   • reportFieldsFromModel — the editable scalar/JSON fields to persist on create
//     (computed tables like table1 / populationByClass are NOT stored; they are
//     recomputed from live data at render time, then overlaid here);
//   • findingRowsFromModel — one persisted finding row per drafted finding, keeping
//     the original auto-draft so the composer's Re-draft can diff;
//   • mergeModel — overlay the persisted edits + numeric overrides onto a freshly
//     computed base model, for preview / DOCX / PDF render.
// Pure; tested in test/features/reports/rtm/rtmPersist.test.ts.
import type { RtmDocModel, RtmDocFinding, RtmTable1Row } from './buildRtmDocModel';
import type { GapTier } from '../../../domain/gaps';

/** The editable fields persisted on an RtmReport row (JSON columns as arrays/objects). */
export interface RtmReportFields {
  title: string;
  reportDate: string;
  signatoryName: string;
  signatoryTitle: string;
  addresseesJson: RtmDocModel['addressees'];
  acknowledgersJson: RtmDocModel['acknowledgers'];
  coverLetterJson: string[];
  keyRecommendationsJson: string[];
  populationInitial: number;
  populationFinal: number;
  populationNote: string;
  classOverridesJson: Record<string, string> | null;
  table1OverridesJson: Record<string, Partial<RtmTable1Row>> | null;
  overallRating: number | null;
  conclusionText: string;
  managementResponse: string;
  unclosedCountOverride: number | null;
  ieCountOverride: number | null;
  ieValueOverride: number | null;
}

/** The editable fields persisted on an RtmFinding row. */
export interface RtmFindingRow {
  seq: number;
  number: string;
  heading: string;
  classification: string;
  findingTitle: string;
  rating: number;
  tier: string;
  criteria: string;
  observation: string;
  risk: string;
  summaryObservation: string;
  rootCausePrimary: string;
  rootCauseSecondary: string;
  rootCauseText: string;
  recommendationsJson: string[];
  linkedRefsJson: string[];
  linkedCriteriaJson: string[];
  subObservationsJson: string[];
  autoDraftJson: RtmDocFinding;
}

/** Extract the persistable editable fields from an auto-draft model (create path). */
export function reportFieldsFromModel(m: RtmDocModel): RtmReportFields {
  return {
    title: m.title,
    reportDate: m.reportDate,
    signatoryName: m.signatoryName,
    signatoryTitle: m.signatoryTitle,
    addresseesJson: m.addressees,
    acknowledgersJson: m.acknowledgers,
    coverLetterJson: m.coverLetter,
    keyRecommendationsJson: m.keyRecommendations,
    populationInitial: m.populationInitial,
    populationFinal: m.populationFinal,
    populationNote: m.populationNote,
    classOverridesJson: null,
    table1OverridesJson: null,
    overallRating: m.overallRating,
    conclusionText: m.conclusionText,
    managementResponse: m.managementResponse,
    unclosedCountOverride: null,
    ieCountOverride: null,
    ieValueOverride: null,
  };
}

/** One persisted finding row per drafted finding, keeping the original auto-draft. */
export function findingRowsFromModel(m: RtmDocModel): RtmFindingRow[] {
  return m.findings.map((f, i) => ({
    seq: i + 1,
    number: f.number,
    heading: f.heading,
    classification: f.classification,
    findingTitle: f.findingTitle,
    rating: f.rating,
    tier: f.tier,
    criteria: f.criteria,
    observation: f.observation,
    risk: f.risk,
    summaryObservation: f.summaryObservation,
    rootCausePrimary: f.rootCausePrimary,
    rootCauseSecondary: f.rootCauseSecondary,
    rootCauseText: '',
    recommendationsJson: f.recommendations,
    linkedRefsJson: f.linkedRefs,
    linkedCriteriaJson: [],
    subObservationsJson: [],
    autoDraftJson: f,
  }));
}

/** Map a persisted finding row back to the render-time RtmDocFinding shape. */
function rowToDocFinding(r: RtmFindingRow): RtmDocFinding {
  return {
    number: r.number,
    heading: r.heading,
    classification: r.classification,
    findingTitle: r.findingTitle,
    rating: r.rating,
    tier: r.tier as GapTier,
    criteria: r.criteria,
    observation: r.observation,
    risk: r.risk,
    rootCausePrimary: r.rootCausePrimary,
    rootCauseSecondary: r.rootCauseSecondary,
    recommendations: r.recommendationsJson ?? [],
    linkedRefs: r.linkedRefsJson ?? [],
    summaryObservation: r.summaryObservation,
  };
}

/**
 * Overlay the persisted report edits + finding edits + numeric overrides onto a
 * freshly computed base model (recomputed from live data). The base supplies the
 * computed tables (table1, populationByClass); the persisted rows supply every
 * consultant-editable value; a present override wins over the computed figure.
 */
export function mergeModel(
  base: RtmDocModel,
  report: RtmReportFields,
  findingRows: readonly RtmFindingRow[],
): RtmDocModel {
  const overrides = report.table1OverridesJson ?? {};
  const applyT1 = (row: RtmTable1Row): RtmTable1Row => {
    const o = overrides[row.rtmClass];
    return o ? { ...row, ...o } : row;
  };
  return {
    ...base,
    title: report.title,
    reportDate: report.reportDate,
    signatoryName: report.signatoryName,
    signatoryTitle: report.signatoryTitle,
    addressees: report.addresseesJson ?? base.addressees,
    acknowledgers: report.acknowledgersJson ?? base.acknowledgers,
    coverLetter: report.coverLetterJson ?? base.coverLetter,
    keyRecommendations: report.keyRecommendationsJson ?? base.keyRecommendations,
    populationInitial: report.populationInitial,
    populationFinal: report.populationFinal,
    populationNote: report.populationNote,
    overallRating: report.overallRating,
    conclusionText: report.conclusionText,
    managementResponse: report.managementResponse,
    table1: base.table1.map(applyT1),
    table1Total: applyT1(base.table1Total),
    findings: findingRows.length ? [...findingRows].sort((a, b) => a.seq - b.seq).map(rowToDocFinding) : base.findings,
    unclosedCount: report.unclosedCountOverride ?? base.unclosedCount,
    ieCount: report.ieCountOverride ?? base.ieCount,
    ieValue: report.ieValueOverride ?? base.ieValue,
  };
}
